import React, { useState } from 'react';
import { Check, Edit2, Trash2, X, Save } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker } from '../../utils/localizedContent';


const TIME_SLOTS = ["10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00"];

const statusColor = {
  confirmed: 'bg-[#10B981]/20 text-[#34D399]',
  cancelled: 'bg-[#EF4444]/20 text-[#EF4444]',
  pending:   'bg-[#F59E0B]/20 text-[#F59E0B]',
  completed: 'bg-[#3B82F6]/20 text-[#3B82F6]',
};

export default function AdminGameReservations({ reservations, setReservations, lang, setMessage }) {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ date: '', time_slot: '', party_size: 2, status: 'confirmed' });

  const l = createTextPicker(lang);
  const openNativeDatePicker = (event) => {
    const input = event.currentTarget;
    if (typeof input?.showPicker === 'function') input.showPicker();
  };

  const handleConfirm = async (id) => {
    try {
      await axios.patch(`${API}/admin/game-reservations/${id}/status`, null, { params: { status: 'confirmed' } });
      setReservations(prev => prev.map(r => r.reservation_id === id ? { ...r, status: 'confirmed' } : r));
      setMessage(l('Reserva confirmada', 'Reservation confirmed'));
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm(l('¿Cancelar esta reserva y avisar al usuario por email?', 'Cancel this reservation and notify user by email?'))) return;
    try {
      await axios.patch(`${API}/admin/game-reservations/${id}/status`, null, { params: { status: 'cancelled' } });
      setReservations(prev => prev.filter(r => r.reservation_id !== id));
      setMessage(l('Reserva cancelada', 'Reservation cancelled'));
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(l('¿Borrar definitivamente esta reserva? Esta acción no se puede deshacer.', 'Delete this reservation permanently? This action cannot be undone.'))) return;
    try {
      await axios.delete(`${API}/admin/game-reservations/${id}`);
      setReservations(prev => prev.filter(r => r.reservation_id !== id));
      setMessage(l('Reserva borrada', 'Reservation deleted'));
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  const openEdit = (r) => {
    setEditingId(r.reservation_id);
    setEditForm({
      date: r.date || '',
      time_slot: r.time_slot || '',
      party_size: r.party_size || 2,
      status: r.status || 'confirmed',
    });
  };

  const handleSaveEdit = async (id) => {
    try {
      // Actualizar estado
      await axios.patch(`${API}/admin/game-reservations/${id}/status`, null, { params: { status: editForm.status } });
      setReservations(prev => prev.map(r =>
        r.reservation_id === id ? { ...r, ...editForm } : r
      ));
      setMessage(l('Reserva actualizada', 'Reservation updated'));
      setEditingId(null);
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white font-bold text-xl">
          {l('Reservas de Juegos', 'Game Reservations')} ({reservations.length})
        </h2>
      </div>
      <div className="space-y-3">
        {reservations.length === 0 ? (
          <p className="text-[#A1A1AA] text-center py-8">{l('No hay reservas', 'No reservations')}</p>
        ) : reservations.map(r => (
          <div key={r.reservation_id} className="bg-[#12121A] border border-white/5 rounded-xl p-4"
               data-testid={`admin-gameres-${r.reservation_id}`}>
            <div className="flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate">{r.game_name || r.product_name || 'Juego'}</p>
                <p className="text-[#A1A1AA] text-sm">
                  {r.date} · {r.time_slot} · {r.duration_hours}h · {r.party_size || 1} {l('pers.', 'ppl')}
                </p>
                <p className="text-[#52525B] text-xs">{r.user_name || r.user_email || 'Usuario'}</p>
                {r.has_linked_table_reservation ? (
                  <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-[11px]">
                    <span className="font-semibold">{l('Mesa asociada:', 'Linked table:')}</span>
                    <span>
                      {r.linked_table_numbers?.length
                        ? `${l('Mesa(s)', 'Table(s)')} ${r.linked_table_numbers.join(', ')}`
                        : l('Mesa reservada automáticamente', 'Table auto-reserved')}
                    </span>
                  </div>
                ) : (
                  <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[11px]">
                    <span className="font-semibold">{l('Mesa asociada:', 'Linked table:')}</span>
                    <span>{l('Sin mesa vinculada', 'No linked table')}</span>
                  </div>
                )}
              </div>

              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${statusColor[r.status] || 'bg-white/10 text-white'}`}>
                {r.status}
              </span>

              <div className="flex items-center gap-1 flex-shrink-0">
                {r.status === 'pending' && (
                  <button onClick={() => handleConfirm(r.reservation_id)}
                    className="p-1.5 rounded-lg text-[#10B981] hover:bg-green-500/10 transition-colors"
                    title={l('Confirmar', 'Confirm')}>
                    <Check size={14} />
                  </button>
                )}
                {/* Edit button */}
                <button
                  onClick={() => editingId === r.reservation_id ? setEditingId(null) : openEdit(r)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    editingId === r.reservation_id
                      ? 'text-[#C9A962] bg-[#C9A962]/10'
                      : 'text-[#A1A1AA] hover:bg-[#C9A962]/10 hover:text-[#C9A962]'
                  }`}
                  title={l('Editar', 'Edit')}
                >
                  <Edit2 size={14} />
                </button>
                {/* Cancel reservation (soft delete + email) */}
                <button
                  onClick={() => handleCancel(r.reservation_id)}
                  className="p-1.5 rounded-lg text-[#F59E0B] hover:bg-amber-500/10 transition-colors"
                  title={l('Cancelar reserva', 'Cancel reservation')}
                >
                  <X size={14} />
                </button>
                {/* Delete reservation (hard delete) */}
                <button
                  onClick={() => handleDelete(r.reservation_id)}
                  className="p-1.5 rounded-lg text-[#EF4444] hover:bg-red-500/10 transition-colors"
                  title={l('Borrar reserva', 'Delete reservation')}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            {/* Inline edit panel */}
            {editingId === r.reservation_id && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[#52525B] text-xs mb-1">{l('Fecha', 'Date')}</label>
                    <input type="date" value={editForm.date}
                      onChange={e => setEditForm(f => ({...f, date: e.target.value}))}
                      onClick={openNativeDatePicker}
                      onFocus={openNativeDatePicker}
                      className="w-full bg-[#1C1C28] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A962]" />
                  </div>
                  <div>
                    <label className="block text-[#52525B] text-xs mb-1">{l('Hora', 'Time slot')}</label>
                    <select value={editForm.time_slot}
                      onChange={e => setEditForm(f => ({...f, time_slot: e.target.value}))}
                      className="w-full bg-[#1C1C28] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A962]">
                      {TIME_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#52525B] text-xs mb-1">{l('Nº personas', 'Party size')}</label>
                    <input type="number" min={1} max={20} value={editForm.party_size}
                      onChange={e => setEditForm(f => ({...f, party_size: parseInt(e.target.value)||1}))}
                      className="w-full bg-[#1C1C28] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A962]" />
                  </div>
                  <div>
                    <label className="block text-[#52525B] text-xs mb-1">{l('Estado', 'Status')}</label>
                    <select value={editForm.status}
                      onChange={e => setEditForm(f => ({...f, status: e.target.value}))}
                      className="w-full bg-[#1C1C28] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A962]">
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => handleSaveEdit(r.reservation_id)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-[#C9A962] text-[#0A1612] rounded-lg text-sm font-semibold hover:bg-[#B8984E]">
                    <Save size={13} />{l('Guardar', 'Save')}
                  </button>
                  <button onClick={() => setEditingId(null)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-white/5 text-[#A1A1AA] rounded-lg text-sm hover:bg-white/10">
                    <X size={13} />{l('Cancelar', 'Cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
